// ======================================================
// Updated by: Halimunnisa
// Feature: Customer Acceptance Workflow
// Purpose: Implemented active queue logic and dynamic targetLeadStatus transition routing.
// ======================================================
import React, { useState, useMemo } from 'react';
import { DataTable } from "@/components/ui";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";
import CustomerAcceptanceModal from '../modals/CustomerAcceptanceModal';
import CustomerAcceptanceControlPanel from '../components/CustomerAcceptanceControlPanel';
import api from '../../../../services/api';
import { showApiError, showSuccessMessage } from '@/lib/toast';
import { exportToExcel } from "@/utils/exportUtils";

const CustomerAcceptanceScreen = ({ leads, isLoading, onRefresh }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  
  const [localSearch, setLocalSearch] = useState('');
  const [localFilters, setLocalFilters] = useState({ date: 'all', fromDate: '', toDate: '' });

  const filteredLeads = useMemo(() => {
    if (!leads) return [];
    
    // ======================================================
    // Updated by: Halimunnisa
    // Active queue filtering added
    // Prevents cumulative stage visibility
    // ======================================================
    let result = leads.filter(l => l.leadStatus === 'cust_acceptance' || l.status === 'cust_acceptance');

    if (localSearch) {
      const lowerSearch = localSearch.toLowerCase();
      result = result.filter(l => 
        l.customId?.toLowerCase().includes(lowerSearch) ||
        l.customerName?.toLowerCase().includes(lowerSearch) ||
        l.firstName?.toLowerCase().includes(lowerSearch) ||
        l.email?.toLowerCase().includes(lowerSearch)
      );
    }
    // Minimal date filtering loop reusing existing logic pattern from RoleSelectionScreen
    return result;
  }, [leads, localSearch, localFilters]);
  //updated by: Halimunnisa
  //Customer Acceptance workflow
  //The filteredLeads useMemo now includes an active queue filter that ensures only leads currently in the 'cust_acceptance' stage are displayed. This prevents leads from previous stages that might still have 'cust_acceptance' status from appearing in this queue, ensuring a clean and accurate representation of the current workload for the Customer Acceptance team.

  const handleExport = async () => {
    if (isExporting) return;
    try {
      setIsExporting(true);
      const exportData = filteredLeads.map(l => ({
        "Verve ID": l.customId || "—",
        "Customer Name": l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name",
        "Phone": l.phoneNumber || l.phone || "—",
        "Email": l.email || "—",
        "Status": l.customerAcceptance?.status || "Pending",
        "Remarks": l.customerAcceptance?.remarks || "—",
        "Date": l.createdAt ? new Date(l.createdAt).toLocaleDateString() : "—",
      }));

      exportToExcel(exportData, {
        filename: `Customer_Acceptance_Export_${new Date().toLocaleDateString().replace(/\//g, "-")}`,
      });
      showSuccessMessage("Export successful!");
    } catch (err) {
      showApiError(err);
    } finally {
      setIsExporting(false);
    }
  };

  const handleOpenModal = (lead) => {
    setSelectedLead(lead);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedLead(null);
  };

  // ======================================================
  // Updated by: Halimunnisa
  // Feature: Customer Acceptance Workflow
  // Purpose: Performs the PATCH request to update the embedded customerAcceptance object and triggers a global status transition, which intrinsically invokes VerveLeadActivity creation via updateLeadService.
  // ======================================================
  const handleConfirmAction = async ({ status, notes }) => {
    if (!selectedLead) return;
    
    setActionLoading(true);
    try {
      const currentAcceptance = selectedLead.customerAcceptance || {};
      
      const updatedAcceptance = {
        ...currentAcceptance,
        status,
        remarks: notes,
        actedAt: new Date()
      };

      // ======================================================
      // Updated by: Halimunnisa
      // Feature: Customer Acceptance Workflow
      // Purpose: Customer Acceptance workflow transition
      // Accepted -> Design
      // Rejected -> Rejected/Stopped (remains in cust_acceptance)
      // Revision Requested -> Initial Quotation Approval
      // ======================================================
      let targetLeadStatus = 'cust_acceptance';
      let actionLog = 'Customer Acceptance updated';
      if (status === 'Accepted') {
         targetLeadStatus = 'design';
         actionLog = 'Customer accepted quotation';
      } else if (status === 'Revision Requested') {
         targetLeadStatus = 'customer_acceptance'; // Key for Initial Quotation Approval
         actionLog = 'Customer requested revision';
      }

      await api.patch(`/verve-leads/${selectedLead._id}`, {
        customerAcceptance: updatedAcceptance,
        status: targetLeadStatus,
        notes: actionLog + (notes ? ` | Remarks: ${notes}` : '')
      });
      
      showSuccessMessage(`Customer Acceptance recorded as ${status}`);
      setIsModalOpen(false);
      setSelectedLead(null);
      
      if (onRefresh) {
        onRefresh();
      } else {
        window.location.reload();
      }
    } catch (err) {
      showApiError(err);
    } finally {
      setActionLoading(false);
    }
  };

  // ======================================================
  // Updated by: Halimunnisa
  // Feature: Customer Acceptance Workflow
  // Purpose: Aligned Customer Acceptance status and action rendering with Design Approval UI.
  // ======================================================
  const getCustomerAcceptanceStatus = (acceptance) => {
    const status = acceptance?.status || 'Pending';
    if (status === 'Accepted') return { text: "ACCEPTED", color: "text-green-700 bg-green-100" };
    if (status === 'Revision Requested') return { text: "REVISION REQUESTED", color: "text-orange-700 bg-orange-100" };
    return { text: "REQUESTED", color: "text-blue-700 bg-blue-100" };
  };

  const columns = useMemo(() => [
    {
      header: "Verve ID",
      key: "customId",
      minWidth: "170px",
      render: (l) => (
        <span className="text-[10px] uppercase tracking-tight whitespace-nowrap">
          {l.customId || "—"}
        </span>
      )
    },
    {
      header: "Contact Details",
      key: "customerName",
      render: (l) => {
        const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
        return (
          <div className="flex flex-col min-w-0 py-1">
            <span className="text-brand-dark tracking-tight text-[13px] font-medium leading-tight truncate">
              {fullName}
            </span>
            <span className="text-[11px] text-slate-500 truncate flex items-center mt-0.5 tracking-tighter">
              {l.phoneNumber || l.phone || "No Phone"}
            </span>
          </div>
        );
      }
    },
    {
      header: "Status",
      key: "status",
      render: (l) => {
        const acceptance = l.customerAcceptance || {};
        const status = getCustomerAcceptanceStatus(acceptance);
        
        return (
          <span className={`text-[10px] px-2 py-1 rounded-md font-bold uppercase tracking-wider ${status.color}`}>
            {status.text}
          </span>
        );
      }
    },
    {
      header: "Actions",
      key: "actions",
      render: (l) => {
        const acceptance = l.customerAcceptance || {};
        const status = acceptance.status || 'Pending';

        if (status === 'Accepted') {
          return (
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-1 rounded border border-green-200">Already Accepted</span>
            </div>
          );
        }

        if (status === 'Revision Requested') {
          return <span className="text-xs font-bold text-orange-600 bg-orange-50 px-2 py-1 rounded border border-orange-200">Revision Requested</span>;
        }

        // ======================================================
        // Updated by: Halimunnisa
        // Feature: Customer Acceptance Workflow
        // Purpose: Triggers the CustomerAcceptanceModal for capturing the final decision, styled exactly like Design Approval.
        // ======================================================
        return (
          <div className="flex items-center gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); handleOpenModal(l); }}
              className="px-3 py-1.5 bg-yellow-500 text-white text-[11px] font-semibold rounded hover:bg-yellow-600 transition-colors shadow-sm"
            >
              Customer Acceptance
            </button>
          </div>
        );
      }
    }
  ], []);

  return (
    <div className="flex flex-col gap-4">
      {/* ======================================================
      Updated by: Halimunnisa
      Feature: Customer Acceptance Workflow
      Purpose: Refactored Customer Acceptance UI to match Design Approval layout exactly.
      Restored local filter section which is now the primary filter for this active queue.
      ====================================================== */}
      <CustomerAcceptanceControlPanel
        search={localSearch}
        setSearch={setLocalSearch}
        filters={localFilters}
        setFilters={setLocalFilters}
        onExport={handleExport}
        isExporting={isExporting}
      />

      <div className="w-full h-full bg-white rounded-xl shadow-sm border border-slate-200/60 overflow-hidden">
        <DataTable columns={columns} data={filteredLeads} isLoading={isLoading} className="border-none shadow-none rounded-none" />
        <CustomerAcceptanceModal isOpen={isModalOpen} onClose={handleCloseModal} onConfirm={handleConfirmAction} loading={actionLoading} />
      </div>
    </div>
  );
};

export default CustomerAcceptanceScreen;
